import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { format, parseISO, addMonths } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Clock, CheckCircle, ChevronLeft, Loader2, BadgeCheck, BadgeX, Heart, Mail } from "lucide-react";

const BIKE_CHOICES = ["Trishaw Only", "Wheelchair Bike Only", "Trishaw and Wheelchair Bike"];

export default function BookingPage() {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maintenance, setMaintenance] = useState(false);
  const [sponsor, setSponsor] = useState({ enabled: false, message: "", url: "" });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    meeting_point: "", time_flexibility: "", extra_info: "",
    trishaw_consent: false, request_consent: false,
  });
  const [bookingCode, setBookingCode] = useState("");
  const [codeStatus, setCodeStatus] = useState(null); // null | "valid" | "invalid"
  const [codeChecking, setCodeChecking] = useState(false);
  const [bikeChoice, setBikeChoice] = useState("Trishaw Only");
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [portalUrl, setPortalUrl] = useState("");
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);
  const debounceRef = useRef(null);

  const isGroup = codeStatus === "valid";

  // Render Turnstile widget when step 2 is shown
  const renderTurnstile = useCallback(() => {
    if (!turnstileRef.current || !window.turnstile) return;
    // Reset any existing widget
    if (turnstileWidgetId.current !== null) {
      try { window.turnstile.remove(turnstileWidgetId.current); } catch (e) { console.debug("turnstile remove (booking render):", e?.message); }
      turnstileWidgetId.current = null;
    }
    setTurnstileToken("");
    turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
      sitekey: "0x4AAAAAAC86gAJOKBga3OaP",
      callback: (token) => setTurnstileToken(token),
      "expired-callback": () => setTurnstileToken(""),
      "error-callback": () => setTurnstileToken(""),
      theme: "light",
    });
  }, []);

  const fetchSlots = () => {
    setLoading(true);
    api.getAvailableSlots()
      .then((r) => setSlots(r.data.slots))
      .catch(() => toast.error("Failed to load available times"))
      .finally(() => setLoading(false));
  };

  const isAdmin = !!localStorage.getItem("admin_token");

  useEffect(() => {
    api.getMaintenanceStatus()
      .then((r) => { if (r.data.maintenance_mode && !isAdmin) setMaintenance(true); })
      .catch(() => { /* non-critical */ });
    fetchSlots();
    api.getEmbedConfig()
      .then((r) => setSponsor({ enabled: r.data.sponsor_message_enabled, message: r.data.sponsor_message || "", url: r.data.sponsor_url || "" }))
      .catch(() => { /* embed config optional */ });
  }, []);

  // Debounced booking code validation
  useEffect(() => {
    if (!bookingCode.trim()) { setCodeStatus(null); return; }
    clearTimeout(debounceRef.current);
    setCodeChecking(true);
    debounceRef.current = setTimeout(() => {
      api.validateBookingCode(bookingCode.trim())
        .then((r) => setCodeStatus(r.data.valid ? "valid" : "invalid"))
        .catch(() => setCodeStatus("invalid"))
        .finally(() => setCodeChecking(false));
    }, 500);
  }, [bookingCode]);

  // Render Turnstile widget when entering step 2
  useEffect(() => {
    if (step !== 2) return;
    // Small delay to ensure DOM ref is available
    const timer = setTimeout(renderTurnstile, 200);
    return () => clearTimeout(timer);
  }, [step, renderTurnstile]);

  const availableDateSet = useMemo(() => new Set(slots.map((s) => s.date)), [slots]);
  const slotsForDate = useMemo(() => {
    if (!selectedDate) return [];
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    return slots.filter((s) => s.date === dateStr);
  }, [slots, selectedDate]);
  const isDateDisabled = (date) => !availableDateSet.has(format(date, "yyyy-MM-dd"));
  const firstAvailableDate = useMemo(() => slots.length ? parseISO(slots[0].date) : undefined, [slots]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSlot) return;
    if (!turnstileToken) {
      toast.error("Please complete the human verification challenge.");
      return;
    }
    setSubmitting(true);
    try {
      const bookingData = {
        requester_name: form.name,
        requester_email: form.email,
        requester_phone: form.phone,
        booking_datetime_iso: selectedSlot.datetime_utc,
        meeting_point: isGroup ? "Coffee@The Edge" : form.meeting_point,
        time_flexibility: isGroup ? "" : form.time_flexibility,
        trishaw_consent: isGroup ? true : form.trishaw_consent,
        request_consent: form.request_consent,
        booking_code: isGroup ? bookingCode.trim() : null,
        bike_choice: bikeChoice,
        extra_info: form.extra_info,
        turnstile_token: turnstileToken,
      };
      const res = await (isAdmin ? api.createBookingAsAdmin(bookingData) : api.createBooking(bookingData));
      if (res.data?.portal_url) setPortalUrl(res.data.portal_url);
      fetchSlots();
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit booking. Please try again.");
      // Reset turnstile on failure
      if (turnstileWidgetId.current !== null && window.turnstile) {
        try { window.turnstile.reset(turnstileWidgetId.current); } catch (e) { console.debug("turnstile reset (booking submit):", e?.message); }
      }
      setTurnstileToken("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ fontFamily: "'Public Sans', sans-serif" }} className="min-h-screen bg-[#fcfdfc]">
      {/* Hero */}
      <div className="h-52 md:h-72 bg-[#dcfce7] flex flex-col justify-center items-center px-4">
        <div className="flex flex-col justify-center items-center text-green-900 px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-center tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Book the Accessible Bike</h1>
          <p className="text-lg mt-2 text-green-700 font-medium">Dodder Valley Cycling</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-10">
        {/* Maintenance mode message */}
        {maintenance && (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Temporarily Unavailable</h2>
            <p className="text-slate-500 text-lg">Booking is temporarily unavailable for maintenance.</p>
            <p className="text-slate-400 mt-2">Please check back shortly.</p>
          </div>
        )}

        {/* Step 1: Calendar & Time */}
        {!maintenance && step === 1 && (
          <div className="animate-in fade-in duration-300">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Choose a Date & Time</h2>
              <p className="text-slate-500 mt-2">Available slots start 36 hours from now to allow volunteer confirmation.</p>
            </div>
            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-green-700" /></div>
            ) : slots.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-green-100 shadow-sm">
                <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 font-medium">No available slots at the moment.</p>
                <p className="text-slate-400 text-sm mt-1">Please check back later or contact us directly.</p>
              </div>
            ) : (
              <>
                <div className="bg-white rounded-2xl border border-green-100 shadow-sm p-4 flex justify-center mb-6">
                  <Calendar mode="single" selected={selectedDate} onSelect={(d) => { setSelectedDate(d); setSelectedSlot(null); }}
                    disabled={isDateDisabled} defaultMonth={firstAvailableDate}
                    fromDate={new Date()} toDate={addMonths(new Date(), 3)} data-testid="booking-calendar" />
                </div>
                {selectedDate && (
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <h3 className="text-lg font-semibold text-slate-700 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      Available times on {format(selectedDate, "EEEE, dd MMMM")}
                    </h3>
                    {slotsForDate.length === 0 ? (
                      <p className="text-slate-400 text-center py-6">No available slots for this date.</p>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="time-slots-grid">
                        {slotsForDate.map((slot) => (
                          <button key={slot.datetime_utc} data-testid={`time-slot-${slot.time}`}
                            onClick={() => setSelectedSlot(slot)}
                            className={`p-3 rounded-xl border-2 text-center font-semibold transition-all duration-200 hover:-translate-y-0.5 active:scale-95 ${
                              selectedSlot?.datetime_utc === slot.datetime_utc
                                ? "border-green-600 bg-green-50 text-green-800 shadow-md ring-4 ring-green-100"
                                : "border-green-200 bg-white text-slate-700 hover:border-green-500 hover:shadow"
                            }`}>{slot.time_label}</button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {selectedSlot && (
                  <div className="mt-6 animate-in fade-in duration-300">
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4 flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
                      <span className="text-green-800 font-medium">Selected: {selectedSlot.label} at {selectedSlot.time_label}</span>
                    </div>
                    <Button data-testid="continue-to-details-btn" onClick={() => setStep(2)}
                      className="w-full h-14 text-lg font-semibold rounded-full bg-[#15803d] hover:bg-green-800 text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5">
                      Continue to Your Details
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Step 2: Details Form */}
        {step === 2 && (
          <div className="animate-in fade-in duration-300">
            <button onClick={() => setStep(1)} className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6 transition-colors" data-testid="back-to-calendar-btn">
              <ChevronLeft className="w-4 h-4" /> Back to calendar
            </button>
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-8">
              <p className="text-green-800 font-semibold">Booking: {selectedSlot?.label} at {selectedSlot?.time_label}</p>
            </div>
            <h2 className="text-3xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>Your Details</h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label className="text-slate-600 font-medium mb-2 block" htmlFor="name">Full Name</Label>
                <Input id="name" data-testid="input-name" type="text" placeholder="e.g. Jane Smith"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required className="h-12 text-base bg-slate-50 border-slate-200 focus:bg-white focus:border-green-600" />
              </div>
              <div>
                <Label className="text-slate-600 font-medium mb-2 block" htmlFor="email">Email Address</Label>
                <Input id="email" data-testid="input-email" type="email" placeholder="e.g. jane@example.com"
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required className="h-12 text-base bg-slate-50 border-slate-200 focus:bg-white focus:border-green-600" />
              </div>
              <div>
                <Label className="text-slate-600 font-medium mb-2 block" htmlFor="phone">Phone Number</Label>
                <Input id="phone" data-testid="input-phone" type="tel" placeholder="e.g. 086 123 4567"
                  value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  required className="h-12 text-base bg-slate-50 border-slate-200 focus:bg-white focus:border-green-600" />
              </div>

              {/* Booking Code */}
              <div>
                <Label className="text-slate-600 font-medium mb-2 block" htmlFor="booking_code">Booking Code</Label>
                <div className="relative">
                  <Input id="booking_code" data-testid="input-booking-code" type="text"
                    placeholder="If you have a booking code, enter it here, otherwise leave blank."
                    value={bookingCode}
                    onChange={(e) => setBookingCode(e.target.value)}
                    className="h-12 text-base bg-slate-50 border-slate-200 focus:bg-white focus:border-green-600 pr-10" />
                  {codeChecking && <Loader2 className="absolute right-3 top-3.5 w-5 h-5 animate-spin text-slate-400" />}
                  {!codeChecking && codeStatus === "valid" && <BadgeCheck className="absolute right-3 top-3.5 w-5 h-5 text-green-600" />}
                  {!codeChecking && codeStatus === "invalid" && <BadgeX className="absolute right-3 top-3.5 w-5 h-5 text-red-500" />}
                </div>
                {codeStatus === "valid" && <p className="text-green-700 text-sm mt-1 font-medium">Group booking code accepted.</p>}
                {codeStatus === "invalid" && <p className="text-red-600 text-sm mt-1">Unrecognised booking code. Please check and try again, or leave blank.</p>}
              </div>

              {/* ── GROUP BOOKING FIELDS ── */}
              {isGroup ? (
                <>
                  {/* Locked meeting point */}
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-1">Meeting Point</p>
                    <p className="text-green-900 font-semibold">Coffee@The Edge</p>
                    <p className="text-green-600 text-xs mt-0.5">Fixed for group bookings</p>
                  </div>

                  {/* Bike Choice */}
                  <div>
                    <Label className="text-slate-600 font-medium mb-2 block" htmlFor="bike_choice">
                      Bike Choice <span className="text-red-500">*</span>
                    </Label>
                    <select id="bike_choice" data-testid="input-bike-choice"
                      value={bikeChoice} onChange={(e) => setBikeChoice(e.target.value)}
                      required
                      className="w-full h-12 text-base bg-slate-50 border border-slate-200 rounded-md px-3 focus:outline-none focus:border-green-600 focus:bg-white">
                      <option value="">Select bike(s) required...</option>
                      {BIKE_CHOICES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </>
              ) : (
                <>
                  {/* Meeting Point */}
                  <div>
                    <Label className="text-slate-600 font-medium mb-2 block" htmlFor="meeting_point">
                      Meeting Point <span className="text-red-500">*</span>
                    </Label>
                    <select id="meeting_point" data-testid="input-meeting-point"
                      value={form.meeting_point} onChange={(e) => setForm({ ...form, meeting_point: e.target.value })}
                      required
                      className="w-full h-12 text-base bg-slate-50 border border-slate-200 rounded-md px-3 focus:outline-none focus:border-green-600 focus:bg-white">
                      <option value="">Select a meeting point...</option>
                      <option value="Meeting Point A - The Weir">Meeting Point A — The Weir</option>
                      <option value="Meeting Point B - Opp. Speaker Connolly">Meeting Point B — Opp. Speaker Connolly</option>
                    </select>
                  </div>

                  {/* Bike Type (single booking) */}
                  <div>
                    <Label className="text-slate-600 font-medium mb-2 block" htmlFor="bike_type">
                      Bike Type <span className="text-red-500">*</span>
                    </Label>
                    <select id="bike_type" data-testid="input-bike-type"
                      value={bikeChoice} onChange={(e) => setBikeChoice(e.target.value)}
                      required
                      className="w-full h-12 text-base bg-slate-50 border border-slate-200 rounded-md px-3 focus:outline-none focus:border-green-600 focus:bg-white">
                      <option value="Trishaw Only">Trishaw</option>
                      <option value="Wheelchair Bike Only">Wheelchair Bike</option>
                    </select>
                  </div>

                  {/* Time Flexibility */}
                  <div>
                    <Label className="text-slate-600 font-medium mb-1 block" htmlFor="time_flexibility">
                      Have you any flexibility in time slot on the day? <span className="text-red-500">*</span>
                    </Label>
                    <p className="text-slate-400 text-sm mb-2">Please let us know exactly what times would suit. If no flexibility just type "no".</p>
                    <textarea id="time_flexibility" data-testid="input-time-flexibility"
                      value={form.time_flexibility} onChange={(e) => setForm({ ...form, time_flexibility: e.target.value })}
                      required rows={3} placeholder='e.g. "A start time between 10am and 1pm" or "no"'
                      className="w-full text-base bg-slate-50 border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:border-green-600 focus:bg-white resize-none" />
                  </div>

                  {/* Trishaw consent */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <label className="flex items-start gap-3 cursor-pointer" htmlFor="trishaw_consent">
                      <input id="trishaw_consent" data-testid="input-trishaw-consent" type="checkbox"
                        checked={form.trishaw_consent} onChange={(e) => setForm({ ...form, trishaw_consent: e.target.checked })}
                        className="mt-1 w-5 h-5 accent-green-700 shrink-0" required />
                      <span className="text-slate-700 text-sm leading-relaxed">
                        <span className="font-semibold">I understand</span> the service is only for those who can't ride bikes because of a particular medical, cognitive or physical challenge. <span className="text-red-500 font-semibold">*</span>
                      </span>
                    </label>
                  </div>
                </>
              )}

              {/* Request consent — always shown */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <label className="flex items-start gap-3 cursor-pointer" htmlFor="request_consent">
                  <input id="request_consent" data-testid="input-request-consent" type="checkbox"
                    checked={form.request_consent} onChange={(e) => setForm({ ...form, request_consent: e.target.checked })}
                    className="mt-1 w-5 h-5 accent-green-700 shrink-0" required />
                  <span className="text-slate-700 text-sm leading-relaxed">
                    <span className="font-semibold">I understand</span> this is a Booking <em>request</em>, and is not a confirmed booking until a follow up confirmation email. <span className="text-red-500 font-semibold">*</span>
                  </span>
                </label>
              </div>

              {/* Extra Information */}
              <div>
                <Label className="text-slate-600 font-medium mb-1 block" htmlFor="extra_info">Extra Information</Label>
                <p className="text-slate-400 text-sm mb-2">If not, leave blank.</p>
                <textarea id="extra_info" data-testid="input-extra-info"
                  value={form.extra_info} onChange={(e) => setForm({ ...form, extra_info: e.target.value })}
                  rows={2} placeholder="Any additional details..."
                  className="w-full text-base bg-slate-50 border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:border-green-600 focus:bg-white resize-none" />
              </div>

              {/* Cloudflare Turnstile human verification */}
              <div className="flex flex-col items-center" data-testid="turnstile-container">
                <div ref={turnstileRef}></div>
                {!turnstileToken && (
                  <p className="text-slate-400 text-xs mt-1">Please verify you are human to continue.</p>
                )}
              </div>

              <div className="pt-2">
                <Button type="submit" data-testid="confirm-booking-btn" disabled={submitting || !turnstileToken}
                  className="w-full h-14 text-lg font-semibold rounded-full bg-[#15803d] hover:bg-green-800 text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5">
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                  {submitting ? "Submitting..." : "Confirm Booking Request"}
                </Button>
                <p className="text-slate-400 text-sm text-center mt-3">A volunteer will be in touch to confirm your booking.</p>
              </div>
            </form>
          </div>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <div className="animate-in fade-in duration-500 text-center py-8" data-testid="booking-success">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-800 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Request Submitted!</h2>
            <p className="text-slate-600 text-lg mb-2">
              Your booking request for <strong>{selectedSlot?.label} at {selectedSlot?.time_label}</strong> has been sent.
            </p>
            <p className="text-slate-500">Our volunteers have been notified. You will receive a confirmation email once a volunteer confirms availability.</p>

            {/* Email notice */}
            <div className="mt-8 mx-auto max-w-md bg-amber-50 border-2 border-amber-300 rounded-2xl p-5 text-left" data-testid="email-notice">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-amber-200 rounded-full flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5 text-amber-700" />
                </div>
                <p className="font-bold text-amber-900 text-base">Check your email</p>
              </div>
              <p className="text-amber-800 text-sm leading-relaxed mb-2">
                All booking notifications will be sent from:
              </p>
              <p className="font-bold text-amber-900 text-base bg-amber-100 rounded-lg px-3 py-2 text-center mb-3">
                bookings@doddervalleycycling.com
              </p>
              <p className="text-amber-700 text-sm leading-relaxed">
                <strong>Can't see an email?</strong> Please check your <strong>Spam</strong> or <strong>Junk</strong> folder. To ensure you receive future notifications, add the above address to your contacts.
              </p>
            </div>

            {portalUrl && (
              <a href={portalUrl} className="mt-5 inline-block text-green-700 font-semibold text-sm hover:underline" data-testid="view-my-bookings-link">
                View My Bookings
              </a>
            )}

            <div className="mt-4">
              <Button
                onClick={() => { setStep(1); setSelectedDate(null); setSelectedSlot(null); setForm({ name: "", email: "", phone: "", meeting_point: "", time_flexibility: "", extra_info: "", trishaw_consent: false, request_consent: false }); setBookingCode(""); setCodeStatus(null); setBikeChoice("Trishaw Only"); setTurnstileToken(""); setPortalUrl(""); fetchSlots(); }}
                className="rounded-full px-8 bg-[#15803d] hover:bg-green-800 text-white" data-testid="book-another-btn">
                Make Another Booking
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-green-100 mt-12 py-6 text-center text-slate-400 text-sm">
        {sponsor.enabled && sponsor.message && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-5 text-amber-800 text-sm text-center max-w-xl mx-auto" data-testid="sponsor-banner">
            <Heart className="w-4 h-4 inline-block mr-1.5 text-amber-600" />
            {sponsor.url ? (
              <a href={sponsor.url} target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-900 transition-colors">{sponsor.message}</a>
            ) : (
              sponsor.message
            )}
          </div>
        )}
        <p>Dodder Valley Cycling - Accessible Bike Booking</p>
        <p className="mt-1">Questions? Email <a href="mailto:bookings@doddervalleycycling.com" className="text-green-700 hover:underline">bookings@doddervalleycycling.com</a></p>
      </div>
    </div>
  );
}
